import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Info, Heart, X, MapPin, Building, Calendar, Clock } from 'lucide-react';
import { Job, JobCardProps } from '@/types/job';
import { motion } from 'framer-motion';

export const JobCard: React.FC<JobCardProps> = ({ 
  job, 
  direction, 
  onViewDetails,
  matchScore
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [offsetX, setOffsetX] = useState(0);
  const [rotation, setRotation] = useState(0);
  const swipeThreshold = 100; // px to trigger swipe action
  
  const handleTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true);
    setStartX(e.touches[0].clientX);
  };
  
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setStartX(e.clientX);
  };
  
  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    const currentX = e.touches[0].clientX;
    const newOffset = currentX - startX;
    setOffsetX(newOffset);
    
    // Calculate rotation based on drag distance (max ±15 degrees)
    const newRotation = (newOffset / (cardRef.current?.offsetWidth || 300)) * 15;
    setRotation(newRotation);
  };
  
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    const currentX = e.clientX;
    const newOffset = currentX - startX;
    setOffsetX(newOffset);
    
    // Calculate rotation based on drag distance (max ±15 degrees)
    const newRotation = (newOffset / (cardRef.current?.offsetWidth || 300)) * 15;
    setRotation(newRotation);
  };
  
  const handleDragEnd = () => {
    if (!isDragging) return;
    
    setIsDragging(false);
    
    // Animate back to center if not enough movement to trigger swipe
    if (Math.abs(offsetX) < swipeThreshold) {
      setOffsetX(0);
      setRotation(0);
      return;
    }
    
    // Keep the offset and rotation for the animation handled by the parent
    // They'll be reset when the next card is shown
  };
  
  // Cleanup on unmount
  useEffect(() => {
    if (!isDragging) {
      // Reset on direction change (after animation completes)
      if (direction) {
        const timeout = setTimeout(() => {
          setOffsetX(0);
          setRotation(0);
        }, 300);
        return () => clearTimeout(timeout);
      }
    }
  }, [direction, isDragging]);
  
  // Get animation styles based on swipe direction
  const getAnimationStyles = () => {
    if (direction === 'left') {
      return {
        x: -window.innerWidth,
        opacity: 0,
        rotate: -30,
        transition: { duration: 0.3 }
      };
    }
    if (direction === 'right') {
      return {
        x: window.innerWidth,
        opacity: 0,
        rotate: 30,
        transition: { duration: 0.3 }
      };
    }
    if (isDragging) {
      return {
        x: offsetX,
        rotate: rotation,
        transition: { duration: 0 }
      };
    }
    return {
      x: 0,
      rotate: 0,
      transition: { 
        type: "spring", 
        stiffness: 300, 
        damping: 20 
      }
    };
  };

  const getLikeIndicatorOpacity = () => {
    if (direction === 'right') return 1;
    return offsetX > 50 ? Math.min(offsetX / 100, 1) : 0;
  };

  const getDislikeIndicatorOpacity = () => {
    if (direction === 'left') return 1;
    return offsetX < -50 ? Math.min(Math.abs(offsetX) / 100, 1) : 0;
  };

  return (
    <motion.div
      ref={cardRef}
      className="w-full absolute max-w-md"
      animate={getAnimationStyles()}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleDragEnd}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleDragEnd}
      onMouseLeave={handleDragEnd}
      initial={false}
    >
      <Card className="overflow-hidden shadow-xl border-0 bg-white/90 backdrop-blur-sm">
        <div className="relative">
          {/* Swipe indicators - like */}
          <motion.div 
            className="absolute top-4 right-4 z-20 bg-green-500/90 rounded-full p-2"
            initial={false}
            animate={{ 
              opacity: getLikeIndicatorOpacity(),
              scale: getLikeIndicatorOpacity() > 0 ? 1 : 0.8
            }}
          >
            <Heart className="h-8 w-8 text-white" />
          </motion.div>
          
          {/* Swipe indicators - dislike */}
          <motion.div 
            className="absolute top-4 left-4 z-20 bg-red-500/90 rounded-full p-2"
            initial={false}
            animate={{ 
              opacity: getDislikeIndicatorOpacity(),
              scale: getDislikeIndicatorOpacity() > 0 ? 1 : 0.8
            }}
          >
            <X className="h-8 w-8 text-white" />
          </motion.div>

          <div className="h-[220px] bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center overflow-hidden">
            {job.logo ? (
              <img 
                src={job.logo} 
                alt={`${job.company} logo`} 
                className="w-full h-full object-cover" 
              />
            ) : (
              <div className="text-white text-3xl font-bold p-6 text-center">
                {job.company}
              </div>
            )}
          </div>
        </div>
        
        <CardContent className="p-6">
          <div className="flex justify-between items-start mb-3">
            <div>
              <h2 className="text-xl font-bold text-gray-900">{job.title}</h2>
              <div className="flex items-center mt-1 text-gray-600">
                <Building className="h-4 w-4 mr-1" />
                <p>{job.company}</p>
              </div>
            </div>
            <Badge variant="outline" className="bg-green-50 text-green-700 ml-2">
              {job.matchScore || matchScore || 0}% Match
            </Badge>
          </div>
          
          <div className="flex flex-wrap gap-2 mb-4">
            {job.remote && (
              <Badge variant="outline" className="bg-purple-50 text-purple-700">
                Remote
              </Badge>
            )}
            <Badge variant="outline" className="flex items-center gap-1">
              <MapPin className="h-3 w-3" /> {job.location}
            </Badge>
            <Badge variant="outline" className="flex items-center gap-1">
              <Clock className="h-3 w-3" /> {job.jobType || job.type}
            </Badge>
          </div>
          
          <div className="text-gray-700 mb-5 line-clamp-3">{job.description}</div>
          
          <div className="flex flex-wrap gap-2 mb-4">
            {job.skills && job.skills.slice(0, 3).map((skill, idx) => (
              <Badge key={idx} variant="secondary" className="bg-sky-50 text-sky-700">
                {skill}
              </Badge>
            ))}
            {job.skills && job.skills.length > 3 && (
              <Badge variant="outline">+{job.skills.length - 3} more</Badge>
            )}
          </div>
          
          <div className="flex justify-between items-center">
            <div className="flex items-center text-sm text-gray-500">
              <Calendar className="h-4 w-4 mr-1" />
              <span>Posted: {job.postedDate || job.posted}</span>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              className="flex items-center gap-1"
              onClick={onViewDetails}
            >
              <Info className="h-4 w-4" />
              Details
            </Button>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};
